import { agaveService } from 'app/services/agave.service';
import { ERPPostingStatus, HighnoteTransactionEvent } from 'API';
import { PostingContext } from 'app/services/agave/erp/posting/types';
import { createPostingSubscription } from 'app/services/agave/erp/posting/sharedPostingUtils';
import { formatAmountForAgavePost } from 'app/services/agave/erpUtils';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';
import { sageIntacctUtil, type SageIntacctExpenseArgs } from 'app/services/agave/erp/agaveSageIntacct';
import { checkAccountingDate } from 'app/pages/overview/tabs/transactions/AccountingDate/AccountingDate';

export interface SageIntacctPostingParams {
  transactionId: string;
  paidolId: string;
  transaction: HighnoteTransactionEvent;
  agaveAccountToken: string;
  last4?: string;
  // SEE-1112: paidol codes against GL dimensions (no Projects module); route
  // cost code/type to classid/itemid in the ccpayitem instead of taskid/costtypeid.
  intacctProjectsNotLicensed?: boolean;
}

/**
 * Builds Sage Intacct CC Transaction payload for queue-based posting.
 * Uses existing sageIntacctUtil.buildCCTransaction for consistency.
 */
export function buildSageIntacctCCTransactionPayload(
  transaction: HighnoteTransactionEvent,
  agaveAccountToken: string,
  last4?: string,
  intacctProjectsNotLicensed?: boolean
): {
  account_token: string;
  ccTransactionPayload: Record<string, unknown>; // Will be converted to XML in backend
  agaveEmployeeId: string | null;
} {
  const paymentNumber = transaction.id.substring(0, 15);
  // Use accountingDate if set, otherwise fall back to transaction createdAt
  const paymentDate = transaction.accountingDate
    ? checkAccountingDate(transaction.accountingDate)
    : new Date(transaction.createdAt).toISOString().split('T')[0];
  const { amount } = formatAmountForAgavePost(transaction.approvedAmount?.value ?? 0, transaction.status);

  // Build expense args for sageIntacctUtil
  const expenseArgs: SageIntacctExpenseArgs = {
    paymentNumber,
    paymentDate,
    amount,
    note: transaction.note ?? null,
    merchantName: transaction.merchantName ?? null,
    last4: last4 ?? null,
    agaveGlCode: transaction.agaveGlCode ?? null,
    agaveProject: transaction.agaveProject ?? null,
    agaveCostType: transaction.agaveCostType ?? null,
    agaveCostCode: transaction.agaveCostCode ?? null,
    agaveCostCodeId: transaction.agaveCostCodeId ?? null,
    agaveCostTypeId: transaction.agaveCostTypeId ?? null,
    agaveGlCodeId: transaction.agaveGlCodeId ?? null,
    agaveProjectId: transaction.agaveProjectId ?? null,
    agaveVendor: transaction.agaveVendor ?? null,
    agaveVendorId: transaction.agaveVendorId ?? null,
    agaveLocation: transaction.agaveLocation ?? null,
    agaveLocationId: transaction.agaveLocationId ?? null,
    agaveDepartment: transaction.agaveDepartment ?? null,
    agaveDepartmentId: transaction.agaveDepartmentId ?? null,
    agaveEmployee: transaction.agaveEmployee ?? null,
    agaveEmployeeId: transaction.agaveEmployeeId ?? null,
  };

  // Build CC transaction using existing utility
  // The chargeCardId will be replaced by the backend with the actual SPEEDCHAIN card ID
  const ccTransactionPayload = sageIntacctUtil.buildCCTransaction(
    expenseArgs,
    sageIntacctUtil.speedchainCreditCardName, // Placeholder, will be replaced in backend
    intacctProjectsNotLicensed ?? false
  );

  return {
    account_token: agaveAccountToken,
    ccTransactionPayload,
    agaveEmployeeId: transaction.agaveEmployeeId ?? null,
  };
}

/**
 * Handles Sage Intacct posting via queue.
 * All complex logic (query card, build XML, post) is handled in the backend.
 */
export async function postSageIntacctTransaction(
  params: SageIntacctPostingParams,
  context: PostingContext
): Promise<void> {
  const {
    transactionId,
    paidolId,
    transaction: txn,
    agaveAccountToken,
    last4,
    intacctProjectsNotLicensed,
  } = params;
  const { dispatch, enqueueSnackbar, t, setIsAgaveLoading, setTransactionPosting, captureException } =
    context;

  setIsAgaveLoading(true);

  try {
    // Build posting data
    const postingData = buildSageIntacctCCTransactionPayload(
      txn,
      agaveAccountToken,
      last4,
      intacctProjectsNotLicensed
    );
    const postingDataString = JSON.stringify(postingData);

    // Set up subscription BEFORE starting the async process
    // This ensures we catch status updates even if posting completes very quickly.
    // When post completes with an employee, persist that as the cardholder's default for future transactions.
    // To persist cardholder default employee on post, do it in the backend (posting Lambda) — direct
    // DDB write on the PaidolUser row. The auto-gen updatePaidolUser mutation no longer exists.
    const subscription = createPostingSubscription(transactionId, context, {
      onCompleted: () => {
        enqueueSnackbar(t('submitExpense.success'), { variant: 'success' });
      },
    });

    // Start the async Sage Intacct posting via the CDK v2-agave Lambda (SEE-308).
    // agaveService throws on transport / GraphQL errors, so we only need to
    // branch on the returned status.
    const result = await agaveService.startSageIntacctPosting({
      input: {
        transactionId,
        paidolId,
        erp: AgaveSourceSystem.SAGE_INTACCT,
        postingType: 'CC_TRANSACTION',
        postingData: postingDataString,
      },
    });

    // Check if mutation succeeded and status is PENDING
    if (result?.status === ERPPostingStatus.PENDING) {
      enqueueSnackbar(t('submitExpense.postingCCTransaction'), { variant: 'info' });
    } else {
      subscription.unsubscribe();
      // If status is not PENDING, it might be FAILED - check the response
      const responseStatus = result?.status;
      const errorMessage = result?.error || 'Failed to start Sage Intacct posting';

      // Update Redux to clear posting state immediately
      const { applyLocalTransactionUpdates } = context;
      dispatch(
        applyLocalTransactionUpdates([
          {
            id: transactionId,
            changes: {
              erpPostingStatus: responseStatus || null,
              erpPostingError: errorMessage,
            },
          },
        ]) as unknown as { type: string; payload: unknown }
      );

      throw new Error(errorMessage);
    }
  } catch (error) {
    // IMPORTANT: Clear posting state FIRST to prevent UI from showing "posting" status
    // This handles GraphQL validation errors that occur before the mutation reaches the backend
    setTransactionPosting({ id: transactionId, isPosting: false });
    setIsAgaveLoading(false);

    captureException(error);
    const errorMessage = (error as Error).message || t('submitExpense.error');
    enqueueSnackbar(errorMessage, { variant: 'error' });

    // Ensure transaction status is updated in Redux
    const { applyLocalTransactionUpdates } = context;
    dispatch(
      applyLocalTransactionUpdates([
        {
          id: transactionId,
          changes: {
            erpPostingStatus: null, // Clear posting status on error
            erpPostingError: errorMessage,
          },
        },
      ]) as unknown as { type: string; payload: unknown }
    );
  }
}
